import { Icon, IconButton, Typography } from '@mui/material';
import { Assessment, DATE_FORMAT_SHORT_FNS, SHORT_FORMATTED_DATE_FORMAT_FNS } from '@schooly/api';
import { AssessmentStatuses } from '@schooly/constants';
import { useAssessmentLabels } from '@schooly/hooks/use-assessment-labels';
import {
  CheckboxSquareIconButton,
  EditIcon,
  ReportGeneratedIcon,
  ReportsIcon,
  TypographyWithOverflowHint,
} from '@schooly/style';
import { newDateTimezoneOffset } from '@schooly/utils/date';
import { format } from 'date-fns';
import { FC, MouseEventHandler, useCallback } from 'react';
import { useIntl } from 'react-intl';
import { Link, useNavigate } from 'react-router-dom';

import { AssessmentReportsTooltip } from '../../../pages/Assessments/AssessmentReportsTooltip';
import { GridRowDate, GridRowItem, GridRowStyled } from '../Grid/Grid';
import {
  ListViewAssessmentsActionsCell,
  ListViewAssessmentsEvaluationCell,
  ListViewAssessmentsNameCell,
  ListViewAssessmentsStatusCell,
} from './ListViewAssessmentsRow.styled';
export interface ListViewAssessmentsRowProps {
  isSelected?: boolean;
  onSelect?: (v: string) => void;
  assessment: Assessment;
  path: string;
  canEdit: boolean;
  showActions?: boolean;
}

export const ListViewAssessmentsRow: FC<ListViewAssessmentsRowProps> = ({
  assessment,
  path,
  canEdit,
  isSelected,
  onSelect,
  showActions,
}) => {
  const navigate = useNavigate();
  const { formatMessage } = useIntl();
  const { getAssessmentEvaluationTitle } = useAssessmentLabels();

  const evaluationTitle = getAssessmentEvaluationTitle(assessment);

  const isPublished = assessment.status === AssessmentStatuses.Published;
  const hasReports = !!assessment.reports.length;

  const handleSelect: MouseEventHandler = useCallback(
    (e) => {
      e.preventDefault();
      onSelect?.(assessment.id);
    },
    [assessment.id, onSelect],
  );

  return (
    <GridRowStyled
      sx={(theme) => ({
        '.date, .checkbox': {
          transition: 'opacity .2s',
        },
        '.checkbox': {
          position: 'absolute',
          left: theme.spacing(2),
        },
        ...(isSelected
          ? {
              '.date': {
                opacity: 0,
              },
            }
          : {
              '&:not(:hover) .checkbox': {
                opacity: 0,
              },
              '&:hover': {
                '.date': {
                  opacity: 0,
                },
                '.checkbox': {
                  opacity: 1,
                },
              },
            }),
      })}
    >
      <Link to={path}>
        <GridRowItem sx={{ position: 'relative' }}>
          <GridRowDate className="date">
            {format(newDateTimezoneOffset(assessment.assessment_date), DATE_FORMAT_SHORT_FNS)}
          </GridRowDate>
          <CheckboxSquareIconButton
            onClick={handleSelect}
            isActive={isSelected}
            className="checkbox"
          />
          <ListViewAssessmentsNameCell>
            <TypographyWithOverflowHint variant="h3">{assessment.name}</TypographyWithOverflowHint>

            {hasReports && (
              <AssessmentReportsTooltip assessment={assessment}>
                <Icon sx={{ color: 'common.grey' }}>
                  {assessment.autogenerated ? <ReportGeneratedIcon /> : <ReportsIcon />}
                </Icon>
              </AssessmentReportsTooltip>
            )}
          </ListViewAssessmentsNameCell>

          <ListViewAssessmentsEvaluationCell isEmpty={!evaluationTitle}>
            <TypographyWithOverflowHint color="text.primary">
              {evaluationTitle ?? '-'}
            </TypographyWithOverflowHint>
          </ListViewAssessmentsEvaluationCell>

          <ListViewAssessmentsStatusCell>
            <Typography color={isPublished ? 'success.main' : 'warning.main'}>
              {isPublished ? (
                <>
                  {formatMessage({ id: 'status-Published' })}{' '}
                  {assessment.publish_date
                    ? format(
                        newDateTimezoneOffset(assessment.publish_date),
                        SHORT_FORMATTED_DATE_FORMAT_FNS,
                      )
                    : ''}
                </>
              ) : (
                formatMessage({ id: 'status-NotPublished' })
              )}
            </Typography>
          </ListViewAssessmentsStatusCell>

          <ListViewAssessmentsActionsCell showActions={showActions}>
            {canEdit && (
              <IconButton
                onClick={(e) => {
                  e.preventDefault();
                  navigate(`/assessments/${assessment.id}/edit`);
                }}
              >
                <EditIcon />
              </IconButton>
            )}
          </ListViewAssessmentsActionsCell>
        </GridRowItem>
      </Link>
    </GridRowStyled>
  );
};
